import React, { useEffect, useState } from "react";
import { MessageCircle, Phone, Plus, Save, Send, Trash2 } from "lucide-react";
import {
  addWaContact,
  deleteWaContact,
  deleteWaMessage,
  getWaMessage,
  listWaContacts,
  saveWaMessage,
} from "../lib/api";

export default function WhatsAppBroadcast() {
  const [message, setMessage] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    getWaMessage().then((d) => { setMessage(d.message || ""); setSavedMessage(d.message || ""); }).catch(() => {});
    listWaContacts().then(setContacts).catch(() => {});
  }, []);

  const flash = (txt) => { setInfo(txt); setTimeout(() => setInfo(""), 2500); };

  const onSaveMessage = async () => {
    setSaving(true);
    setError("");
    try {
      const d = await saveWaMessage(message);
      setMessage(d.message);
      setSavedMessage(d.message);
      flash("Message saved");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save message");
    }
    setSaving(false);
  };

  const onClearMessage = async () => {
    if (!window.confirm("Remove the saved message?")) return;
    try {
      await deleteWaMessage();
      setMessage("");
      setSavedMessage("");
      flash("Message removed");
    } catch { /* ignore */ }
  };

  const onAdd = async () => {
    if (!newPhone.trim()) return;
    setAdding(true);
    setError("");
    try {
      const c = await addWaContact(newName, newPhone);
      setContacts((cs) => [...cs, c]);
      setNewName("");
      setNewPhone("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to add contact");
    }
    setAdding(false);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this number?")) return;
    try {
      await deleteWaContact(id);
      setContacts((cs) => cs.filter((c) => c.id !== id));
    } catch { /* ignore */ }
  };

  const sendTo = (c) => {
    if (!savedMessage.trim()) { alert("Save a message first, then send."); return; }
    window.open(`https://wa.me/${c.phone}?text=${encodeURIComponent(savedMessage)}`, "_blank", "noopener");
  };

  const dirty = message !== savedMessage;

  return (
    <section className="mb-8 glass-deep rounded-3xl p-5 sm:p-7" data-testid="wa-broadcast-panel">
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle size={20} className="text-emerald-700" />
        <h2 className="font-display text-xl text-ink">WhatsApp Broadcast — My Numbers &amp; Message</h2>
      </div>
      <p className="text-xs text-ink-soft mb-4 max-w-3xl">
        Write your own message once, add any WhatsApp numbers below, and send it to anyone
        with one click. You can edit or remove the message any time — Telugu and English both work.
      </p>

      {/* My message */}
      <div className="bg-white/70 rounded-2xl p-4 mb-4">
        <p className="label-eyebrow mb-2">My message (sent to every number)</p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          placeholder="Type your invitation / thank-you message here… (saved once, reused for everyone)"
          className="w-full bg-transparent border border-[#D4AF37]/40 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]"
          style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}
          data-testid="wa-broadcast-message"
        />
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={onSaveMessage}
            disabled={saving || !dirty}
            className="btn-royal text-xs py-1.5 px-4 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="wa-broadcast-save-msg"
          >
            <Save size={12} /> {saving ? "Saving…" : savedMessage ? "Update Message" : "Save Message"}
          </button>
          {savedMessage && (
            <button
              onClick={onClearMessage}
              className="text-xs text-rose-700 hover:underline inline-flex items-center gap-1"
              data-testid="wa-broadcast-clear-msg"
            >
              <Trash2 size={12} /> Remove message
            </button>
          )}
          {info && <span className="text-xs text-emerald-700" data-testid="wa-broadcast-info">{info}</span>}
        </div>
      </div>

      {/* Add number */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Name (optional)"
          className="bg-white/70 border border-stone-300 rounded-lg px-3 py-1.5 text-xs w-40 focus:outline-none focus:border-[#D4AF37]"
          data-testid="wa-broadcast-new-name"
        />
        <input
          type="tel"
          value={newPhone}
          onChange={(e) => setNewPhone(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
          placeholder="+91 98765 43210"
          className="bg-white/70 border border-stone-300 rounded-lg px-3 py-1.5 text-xs w-44 focus:outline-none focus:border-[#D4AF37]"
          data-testid="wa-broadcast-new-phone"
        />
        <button
          onClick={onAdd}
          disabled={adding || !newPhone.trim()}
          className="btn-royal text-xs py-1.5 px-4 disabled:opacity-40 inline-flex items-center gap-1.5"
          data-testid="wa-broadcast-add"
        >
          <Plus size={12} /> {adding ? "Adding…" : "Add Number"}
        </button>
        <span className="text-xs text-ink-soft">{contacts.length} number{contacts.length === 1 ? "" : "s"}</span>
      </div>

      {error && <p className="text-rose-700 text-xs mb-3" data-testid="wa-broadcast-error">{error}</p>}

      {/* Contact list */}
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="wa-broadcast-list">
        {contacts.map((c) => (
          <li
            key={c.id}
            className="flex items-center gap-2 bg-white/70 border border-stone-200 rounded-xl px-3 py-2"
            data-testid={`wa-contact-${c.id}`}
          >
            <Phone size={13} className="text-stone-400 shrink-0" />
            <div className="min-w-0 grow">
              {c.name && <p className="text-xs font-medium text-ink truncate">{c.name}</p>}
              <p className="text-[11px] text-ink-soft font-mono">+{c.phone}</p>
            </div>
            <button
              onClick={() => sendTo(c)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] shrink-0 ${savedMessage.trim() ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-stone-200 text-stone-400"}`}
              title={savedMessage.trim() ? "Open WhatsApp with your message" : "Save a message first"}
              data-testid={`wa-contact-send-${c.id}`}
            >
              <Send size={10} /> Send
            </button>
            <button
              onClick={() => onDelete(c.id)}
              className="text-rose-600 hover:text-rose-800 shrink-0 p-1"
              title="Remove number"
              data-testid={`wa-contact-delete-${c.id}`}
            >
              <Trash2 size={13} />
            </button>
          </li>
        ))}
        {!contacts.length && (
          <li className="col-span-full text-center py-5 text-ink-soft text-xs">
            No numbers yet — add one above to start sending your message.
          </li>
        )}
      </ul>
    </section>
  );
}
